import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import { useAuth } from '../context/AuthContext';
import { getFollowUpList } from '../services/follow-up-service';
import EditFollowUpModal from '../components/EditFollowUpModal';
import DeleteFollowUpModal from '../components/DeleteFollowUpModal';
import FollowUp from '../models/FollowUp';
import { ContactOption } from '../models/enums/ContactOption';
import { useDebounce } from '../utils/CommonUtils';
import FollowUpSearchParams from "../models/FollowUpSearchParams";
import {formatFollowUpTime} from "../utils/DateTimeUtils";

const FollowUpsPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [followUps, setFollowUps] = useState<FollowUp[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<ContactOption[]>([]);
    const [sortBy, setSortBy] = useState<'name' | 'followUpDate'>('followUpDate');
    const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
    const [showFilters, setShowFilters] = useState(false);
    const [errorToast, setErrorToast] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [activeFollowUp, setActiveFollowUp] = useState<FollowUp | null>(null);

    useEffect(() => {
        fetchFollowUps();
    }, [user, page, pageSize, debouncedSearch, dateFrom, dateTo, selectedTypes, sortBy, sortDir]);

    const fetchFollowUps = async () => {
        if (!user) return;
        if (debouncedSearch && debouncedSearch.length < 3) return;

        try {
            const params: FollowUpSearchParams = {
                userId: user.id,
                page: page,
                pageSize: pageSize,
                search: debouncedSearch,
                dateFrom: dateFrom,
                dateTo: dateTo,
                contactOptions: selectedTypes.length > 0 ? selectedTypes : null,
                sortBy: sortBy,
                sortDir: sortDir
            };
            const response = await getFollowUpList(params);
            if (response.status === 200 && response.data) {
                setFollowUps(response.data.followUps);
                setTotalCount(response.data.totalCount);
            } else {
                console.error('Invalid response from api', response);
                setErrorToast('Unable to fetch follow-ups. Please try again later.');
            }
        } catch (err) {
            console.error('Error while fetching follow-ups', err);
            setErrorToast('Unable to fetch follow-ups. Please try again later.');
        }
    };

    const handleUpdateFollowUp = () => {
        fetchFollowUps();
    };

    const handleDeleteFollowUp = (id: number) => {
        setFollowUps(prev => prev.filter(fu => fu.id !== id));
        setTotalCount(prev => prev - 1);
    };

    const handleToggleType = (type: ContactOption) => {
        setSelectedTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
        setPage(1);
    };

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-6">
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                    <input
                        type="text"
                        placeholder="Search follow-ups..."
                        className="border rounded p-2"
                        value={searchTerm}
                        onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                    />

                    <div className="flex items-center space-x-2">
                        <label>Show:</label>
                        <select
                            data-testid="page-size"
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            className="border rounded p-1"
                        >
                            {[10,20,50,100].map(size => (
                                <option key={size} value={size}>{size} per page</option>
                            ))}
                        </select>

                        <label>Sort:</label>
                        <select
                            data-testid='sort-field'
                            value={sortBy}
                            onChange={e => { setSortBy(e.target.value as any); setPage(1); }}
                            className="border rounded p-1"
                        >
                            <option value="name">Client Name</option>
                            <option value="followUpDate">Date</option>
                        </select>
                        <select
                            data-testid="sort-dir"
                            value={sortDir}
                            onChange={e => setSortDir(e.target.value as any)}
                            className="border rounded p-1"
                        >
                            <option value="ASC">Ascending</option>
                            <option value="DESC">Descending</option>
                        </select>

                        <button
                            className={`border rounded p-2 ${showFilters ? 'bg-crumborange text-white' : 'bg-white'}`}
                            onClick={() => setShowFilters(!showFilters)}
                            data-testid="filter-toggle"
                        >
                            Filters
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="border rounded p-4 mb-4" data-testid="filter-panel">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Contact Options */}
                            <div>
                                <label className="block font-medium mb-1">Contact Options:</label>
                                <div className="flex flex-wrap gap-2">
                                    {Object.values(ContactOption).map(opt => (
                                        <button
                                            key={opt}
                                            data-testid={`${opt}-btn`}
                                            type="button"
                                            className={`px-3 py-1 border rounded cursor-pointer ${selectedTypes.includes(opt) ? 'bg-crumborange text-white' : 'bg-white'}`}
                                            onClick={() => handleToggleType(opt)}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Date Range + Reset */}
                            <div>
                                <div className="mb-4">
                                    <label htmlFor="date-from" className="block font-medium mb-1">Date From:</label>
                                    <input
                                        id="date-from"
                                        type="date"
                                        className="border rounded w-full p-2"
                                        max={dateTo}
                                        value={dateFrom}
                                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="date-to" className="block font-medium mb-1">Date To:</label>
                                    <input
                                        id="date-to"
                                        type="date"
                                        className="border rounded w-full p-2"
                                        min={dateFrom}
                                        value={dateTo}
                                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                                    />
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => {
                                            setDateFrom('');
                                            setDateTo('');
                                            setSelectedTypes([]);
                                            setPage(1);
                                        }}
                                        className="px-4 py-2 rounded border"
                                        data-testid="reset-filters"
                                    >
                                        Reset Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border rounded" data-testid="follow-ups-table">
                        <thead className="bg-gray-100 text-left">
                        <tr>
                            <th data-testid="Client-header" className="px-4 py-2">Client</th>
                            <th data-testid="Date-header" className="px-4 py-2">Date</th>
                            <th data-testid="Time-header" className="px-r py-2">Time</th>
                            <th data-testid="Contact-header" className="px-4 py-2">Contact</th>
                            <th data-testid="Notes-header" className="px-4 py-2">Notes</th>
                            <th data-testid="Actions-header" className="px-4 py-2">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {followUps.length > 0 ? followUps.map(followUp => {
                            const isOverdue = followUp.followUpDate < startOfToday;
                            const isDueToday = followUp.followUpDate >= startOfToday && followUp.followUpDate < startOfTomorrow;
                            const timeDisplay = formatFollowUpTime(followUp);
                            return (
                                <tr
                                    key={followUp.id}
                                    data-testid={`follow-up-${followUp.id}`}
                                    className={`border-t hover:bg-gray-50 cursor-pointer ${isOverdue ? 'bg-red-50' : isDueToday ? 'bg-yellow-50' : ''}`}
                                    onClick={() => navigate(`/client/${followUp.clientId}`)}
                                >
                                    <td className="px-4 py-2">{followUp.name}</td>
                                    <td className="px-4 py-2 align-middle">
                                        <div className="inline-flex items-center">
                                            {followUp.followUpDate.toLocaleDateString()}
                                            {isOverdue && <span data-testid="overdue" className="ml-2" title="Overdue">⚠️</span>}
                                            {isDueToday && <span data-testid="due-today" className="ml-2" title="Due today">📅</span>}
                                        </div>
                                    </td>
                                    <td
                                        className="px-4 py-2 align-middle text-gray-700"
                                        data-testid={`follow-up-time-${followUp.id}`}
                                        aria-label="Follow-up time"
                                    >
                                        {timeDisplay ?? '—'}
                                    </td>
                                    <td className="px-4 py-2">{followUp.contactOption}</td>
                                    <td className="px-4 py-2" data-testid={`notes-${followUp.id}`}>{followUp.notes}</td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                setActiveFollowUp(followUp);
                                                setIsEditModalOpen(true);
                                            }}
                                            className="text-crumbblue hover:underline mr-2"
                                            data-testid={`edit-follow-up-${followUp.id}`}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                setActiveFollowUp(followUp);
                                                setIsDeleteModalOpen(true);
                                            }}
                                            className="text-red-500 hover:underline"
                                            data-testid={`delete-follow-up-${followUp.id}`}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={5} data-testid="placeholder" className="p-4 text-center text-gray-500">
                                    No follow-ups found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-4">
                    <div data-testid="page-range">
                        {`Showing ${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
                    </div>
                    <div className="space-x-2">
                        <button
                            onClick={() => setPage(p => Math.max(p - 1, 1))}
                            disabled={page === 1}
                            data-testid='prev-btn'
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Prev
                        </button>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            data-testid="next-btn"
                            disabled={page * pageSize >= totalCount}
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </main>
            <Footer />

            {activeFollowUp && (
                <EditFollowUpModal
                    isOpen={isEditModalOpen}
                    followUp={activeFollowUp}
                    onClose={() => { setIsEditModalOpen(false); setActiveFollowUp(null); }}
                    onError={msg => setErrorToast(msg)}
                    onUpdateFollowUp={() => handleUpdateFollowUp()}
                />
            )}

            {activeFollowUp && activeFollowUp.id && (
                <DeleteFollowUpModal
                    isOpen={isDeleteModalOpen}
                    followUpId={activeFollowUp.id}
                    onClose={() => { setIsDeleteModalOpen(false); setActiveFollowUp(null); }}
                    onError={msg => setErrorToast(msg)}
                    onDelete={(id) => handleDeleteFollowUp(id)}
                />
            )}

            <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />
        </div>
    );
};

export default FollowUpsPage;